
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PRESET_COLORS = [
  '#14B8A6',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#8B5CF6',
  '#3B82F6',
  '#10B981',
  '#F97318',
  '#6366F1',
  '#06B6D4',
  '#84CC16',
  '#D946EF',
];

function SortableCategoryRow({
  category,
  onEdit,
  onToggle,
  onDelete,
  postCount,
}: {
  category: Category;
  onEdit: (cat: Category) => void;
  onToggle: (cat: Category) => void;
  onDelete: (id: string) => void;
  postCount: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b bg-white hover:bg-gray-50 transition-colors"
    >
      <td className="px-4 py-3 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-draggable text-lg"></i>
          </div>
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: category.color }}
          ></div>
          <span className="font-medium text-gray-900">{category.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">{category.slug}</td>
      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
        {category.description || '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 text-center">
        {postCount}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
            category.is_active
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {category.is_active ? '有効' : '無効'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(category)}
            className="text-teal-600 hover:text-teal-800 text-sm cursor-pointer whitespace-nowrap"
          >
            編集
          </button>
          <button
            onClick={() => onToggle(category)}
            className="text-amber-600 hover:text-amber-800 text-sm cursor-pointer whitespace-nowrap"
          >
            {category.is_active ? '無効化' : '有効化'}
          </button>
          <button
            onClick={() => onDelete(category.id)}
            className="text-red-500 hover:text-red-700 text-sm cursor-pointer whitespace-nowrap"
          >
            削除
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [postCounts, setPostCounts] = useState<Record<string, number>>({});

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#14B8A6',
    is_active: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchCategories();
    fetchPostCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchCategories = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      setNotification({
        type: 'error',
        message: 'カテゴリーの取得に失敗しました',
      });
    } else {
      setCategories(data ?? []);
    }
    setLoading(false);
  };

  const fetchPostCounts = async () => {
    const { data, error } = await supabase.from('blog_posts').select('category');

    if (!error && data) {
      const counts: Record<string, number> = {};
      data.forEach((post: any) => {
        const cat = post.category || '';
        counts[cat] = (counts[cat] || 0) + 1;
      });
      setPostCounts(counts);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#14B8A6',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (cat: Category) => {
    setEditingCategory(cat);
    setFormData({
      name: cat.name,
      slug: cat.slug,
      description: cat.description ?? '',
      color: cat.color,
      is_active: cat.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setNotification({
        type: 'error',
        message: 'カテゴリー名を入力してください',
      });
      return;
    }
    if (!formData.slug.trim()) {
      setNotification({
        type: 'error',
        message: 'スラッグを入力してください',
      });
      return;
    }

    setSaving(true);

    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      description: formData.description.trim() || null,
      color: formData.color,
      is_active: formData.is_active,
      updated_at: new Date().toISOString(),
    };

    if (editingCategory) {
      const { error } = await supabase
        .from('categories')
        .update(payload)
        .eq('id', editingCategory.id);

      if (error) {
        setNotification({
          type: 'error',
          message: error.message.includes('unique')
            ? 'この名前またはスラッグは既に使用されています'
            : 'カテゴリーの更新に失敗しました',
        });
      } else {
        if (editingCategory.name !== formData.name.trim()) {
          await supabase
            .from('blog_posts')
            .update({ category: formData.name.trim() })
            .eq('category', editingCategory.name);
        }
        setNotification({
          type: 'success',
          message: 'カテゴリーを更新しました',
        });
        setShowModal(false);
        await fetchCategories();
        await fetchPostCounts();
      }
    } else {
      const maxOrder =
        categories.length > 0
          ? Math.max(...categories.map((c) => c.sort_order)) + 1
          : 0;
      const { error } = await supabase
        .from('categories')
        .insert({ ...payload, sort_order: maxOrder });

      if (error) {
        setNotification({
          type: 'error',
          message: error.message.includes('unique')
            ? 'この名前またはスラッグは既に使用されています'
            : 'カテゴリーの作成に失敗しました',
        });
      } else {
        setNotification({
          type: 'success',
          message: 'カテゴリーを作成しました',
        });
        setShowModal(false);
        await fetchCategories();
      }
    }

    setSaving(false);
  };

  const handleToggle = async (cat: Category) => {
    const { error } = await supabase
      .from('categories')
      .update({
        is_active: !cat.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', cat.id);

    if (error) {
      setNotification({
        type: 'error',
        message: 'ステータスの変更に失敗しました',
      });
    } else {
      setNotification({
        type: 'success',
        message: cat.is_active ? 'カテゴリーを無効化しました' : 'カテゴリーを有効化しました',
      });
      await fetchCategories();
    }
  };

  const handleDelete = async (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (cat && postCounts[cat.name] > 0) {
      setNotification({
        type: 'error',
        message: `このカテゴリーには${postCounts[cat.name]}件の記事があります。先に記事のカテゴリーを変更してください。`,
      });
      setDeleteConfirm(null);
      return;
    }

    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) {
      setNotification({
        type: 'error',
        message: 'カテゴリーの削除に失敗しました',
      });
    } else {
      setNotification({
        type: 'success',
        message: 'カテゴリーを削除しました',
      });
      await fetchCategories();
    }
    setDeleteConfirm(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);
      const newOrder = arrayMove(categories, oldIndex, newIndex);
      setCategories(newOrder);

      try {
        for (let i = 0; i < newOrder.length; i++) {
          await supabase
            .from('categories')
            .update({ sort_order: i })
            .eq('id', newOrder[i].id);
        }
        setNotification({
          type: 'success',
          message: '並び順を保存しました',
        });
      } catch {
        setNotification({
          type: 'error',
          message: '並び順の保存に失敗しました',
        });
        await fetchCategories();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl"></i>
              </div>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">
              カテゴリー設定
            </h1>
          </div>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            新規カテゴリー
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-xl max-w-md ${
            notification.type === 'success'
              ? 'bg-teal-600 text-white'
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <i
                className={`${
                  notification.type === 'success'
                    ? 'ri-checkbox-circle-fill'
                    : 'ri-error-warning-fill'
                } text-xl`}
              ></i>
              <span className="font-semibold">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Info */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center text-teal-600 flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-lg"></i>
          </div>
          <p className="text-sm text-teal-800">
            カテゴリーの追加・編集・並び替えができます。ドラッグで並び順を変更できます。記事が紐づいているカテゴリーは削除できません。
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-400 mr-2"></i>
            <span className="text-gray-500">読み込み中...</span>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
              <i className="ri-folder-line text-5xl"></i>
            </div>
            <p className="text-gray-500 mb-4">カテゴリーがまだありません</p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              最初のカテゴリーを作成
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700">
                      カテゴリー名
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700">
                      スラッグ
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700">
                      説明
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700 text-center">
                      記事数
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700">
                      状態
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-700">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext
                    items={categories.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {categories.map((cat) => (
                      <SortableCategoryRow
                        key={cat.id}
                        category={cat}
                        onEdit={openEditModal}
                        onToggle={handleToggle}
                        onDelete={(id) => setDeleteConfirm(id)}
                        postCount={postCounts[cat.name] || 0}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingCategory ? 'カテゴリー編集' : '新規カテゴリー'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリー名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: editingCategory
                        ? formData.slug
                        : generateSlug(e.target.value),
                    })
                  }
                  placeholder="例：配信機材"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スラッグ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  placeholder="例：equipment"
                />
                <p className="mt-1 text-xs text-gray-400">
                  URLに使用される識別子です
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  説明
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="カテゴリーの説明（任意）"
                  maxLength={500}
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カラー
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full cursor-pointer transition-transform hover:scale-110 ${
                        formData.color === color
                          ? 'ring-2 ring-offset-2 ring-teal-500 scale-110'
                          : ''
                      }`}
                      style={{ backgroundColor: color }}
                    ></button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
                <span className="text-sm text-gray-700">有効</span>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {saving ? '保存中...' : editingCategory ? '更新' : '作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                <i className="ri-delete-bin-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  カテゴリーを削除
                </h3>
                <p className="text-sm text-gray-500">
                  この操作は取り消せません
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
